import { Injectable } from '@angular/core';
import {
    HttpClient,
    HttpHeaders,
    HttpParams
} from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class CrudService {

    // =========================================================
    // LOCAL ENV
    // =========================================================

    // siteUrl: string = 'https://localhost:44375/api/';
    // DownLoadURL: string = 'https://localhost:44375/API/ECRUPLOADEDFILES/';
    // TourFileUrl: string = 'https://localhost:44375/api/UploadedFiles/';
    // profilesiteUrl: string = 'https://localhost:44375/';


    // =========================================================
    // TEST ENV
    // =========================================================

    siteUrl: string = 'https://teamworks.teamcomputers.com/api/API/';


    // =========================================================
    // UAT / COMMON URLS
    // =========================================================

    // siteUrl: string = 'http://10.11.4.6:8885/API/';

    DownLoadURL: string =
        'https://teamworks.teamcomputers.com/image/ECRUPLOADEDFILES/';

    profilesiteUrl: string =
        'https://teamworks.teamcomputers.com/API/';

    TourFileUrl: string =
        'https://teamworks.teamcomputers.com/API/UploadedFiles/';


    // =========================================================
    // LIVE ENV
    // =========================================================

    // siteUrl: string =
    //     'https://teamworks.teamcomputers.com/API/API/';

    // DownLoadURL: string =
    //     'https://teamworks.teamcomputers.com/image/ECRUPLOADEDFILES/';

    // profilesiteUrl: string =
    //     'https://teamworks.teamcomputers.com/API/';

    // TourFileUrl: string =
    //     'https://teamworks.teamcomputers.com/API/UploadedFiles/';


    // =========================================================
    // THIRD PARTY APIs
    // =========================================================

    siteBamUrl: string =
        'https://bam.teamcomputers.com/DarwinBox/';

    SiteBamECRStatusURL: string =
        'https://bam.teamcomputers.com/TW/ECRStatus';

    SiteThrURL: string =
        'https://tara.teamcomputers.com/api/thirdparty/';


    // =========================================================
    // TEAMWORKS API KEY
    // Change only here if key changes
    // =========================================================

    private readonly apiKey: string =
        'TW-19604-A9F8327PM2026';


    constructor(private http: HttpClient) { }

    /** Plain header map for Dropzone / Jodit (they do not use Angular HttpClient). */
    getApiKeyHeaderMap(): { [header: string]: string } {
        return { 'X-Api-Key': this.apiKey };
    }


    // =========================================================
    // COMMON TEAMWORKS HEADER
    // =========================================================

    private getApiHeaders(): HttpHeaders {

        return new HttpHeaders({
            'X-Api-Key': this.apiKey
        });
    }


    // =========================================================
    // MERGE CUSTOM HEADERS WITH API KEY
    // =========================================================

    private mergeHeaders(
        customHeaders?: HttpHeaders
    ): HttpHeaders {

        let headers = this.getApiHeaders();

        if (customHeaders) {

            customHeaders.keys().forEach(key => {

                const values =
                    customHeaders.getAll(key);

                if (values) {

                    values.forEach(value => {

                        headers =
                            headers.append(
                                key,
                                value
                            );

                    });
                }
            });
        }

        return headers;
    }


    // =========================================================
    // GET
    // Automatically sends X-Api-Key
    // =========================================================

    get<T = any>(url: string): Observable<T> {

        return this.http.get<T>(
            this.siteUrl + url,
            {
                headers: this.getApiHeaders()
            }
        );
    }


    // =========================================================
    // GET WITH CUSTOM OPTIONS / HEADERS
    // Automatically adds X-Api-Key also
    // =========================================================

    getWithHeaders(
        url: string,
        options: any = {}
    ): any {

        const customHeaders =
            options && options.headers instanceof HttpHeaders
                ? options.headers
                : undefined;

        const headers =
            this.mergeHeaders(customHeaders);

        return this.http.get(
            this.siteUrl + url,
            {
                ...options,
                headers: headers
            }
        );
    }


    // =========================================================
    // GET WITH PARAMS  
    // Optional helper
    // =========================================================

    getWithParams<T = any>(
        url: string,
        params: HttpParams
    ): Observable<T> {

        return this.http.get<T>(
            this.siteUrl + url,
            {
                headers: this.getApiHeaders(),
                params: params
            }
        );
    }


    // =========================================================
    // POST
    // Automatically sends X-Api-Key unless skipApiKey is true
    // =========================================================

    post<T = any>(
        url: string,
        body: any,
        skipApiKey: boolean = false
    ): Observable<T> {

        return this.http.post<T>(
            this.siteUrl + url,
            body,
            skipApiKey
                ? {}
                : {
                    headers: this.getApiHeaders()
                }
        );
    }


    // =========================================================
    // POST WITH CUSTOM HEADERS
    // Custom headers + X-Api-Key
    // =========================================================

    postWithHeader(
        url: string,
        body: any,
        options: any = {}
    ): any {

        const customHeaders =
            options && options.headers instanceof HttpHeaders
                ? options.headers
                : undefined;

        const headers =
            this.mergeHeaders(customHeaders);

        return this.http.post(
            this.siteUrl + url,
            body,
            {
                ...options,
                headers: headers
            }
        );
    }


    // =========================================================
    // PUT
    // Automatically sends X-Api-Key
    // =========================================================

    put<T = any>(
        url: string,
        body: any
    ): Observable<T> {

        return this.http.put<T>(
            this.siteUrl + url,
            body,
            {
                headers: this.getApiHeaders()
            }
        );
    }


    // =========================================================
    // PUT WITH CUSTOM HEADERS
    // =========================================================

    putWithHeader(
        url: string,
        body: any,
        options: any = {}
    ): any {

        const customHeaders =
            options && options.headers instanceof HttpHeaders
                ? options.headers
                : undefined;

        const headers =
            this.mergeHeaders(customHeaders);

        return this.http.put(
            this.siteUrl + url,
            body,
            {
                ...options,
                headers: headers
            }
        );
    }


    // =========================================================
    // DELETE
    // Automatically sends X-Api-Key
    // =========================================================

    delete<T = any>(
        url: string
    ): Observable<T> {

        return this.http.delete<T>(
            this.siteUrl + url,
            {
                headers: this.getApiHeaders()
            }
        );
    }


    // =========================================================
    // DELETE WITH BODY
    // Use only if required by your API
    // =========================================================

    deleteWithBody<T = any>(
        url: string,
        body: any
    ): Observable<T> {

        return this.http.request<T>(
            'DELETE',
            this.siteUrl + url,
            {
                headers: this.getApiHeaders(),
                body: body
            }
        );
    }


    // =========================================================
    // BAM API
    // DO NOT SEND TEAMWORKS API KEY
    // =========================================================

    bampost<T = any>(
        url: any,
        body: any
    ): Observable<T> {

        return this.http.post<T>(
            'https://bam.teamcomputers.com/' + url,
            body
        );
    }


    // =========================================================
    // WAP API
    // DO NOT SEND TEAMWORKS API KEY
    // =========================================================

    wappost<T = any>(
        url: any,
        body: any
    ): Observable<T> {

        return this.http.post<T>(
            'https://wap.teamcomputers.com/emaauth/api/' + url,
            body
        );
    }


    // =========================================================
    // BAM DARWINBOX API
    // DO NOT SEND TEAMWORKS API KEY
    // =========================================================

    postBam<T = any>(
        url: string,
        body: any
    ): Observable<T> {

        return this.http.post<T>(
            this.siteBamUrl + url,
            body
        );
    }


    // =========================================================
    // THR THIRD PARTY API
    // Uses its own token
    // =========================================================

    postThr<T = any>(
        url: string,
        body: any
    ): Observable<T> {

        const headers =
            new HttpHeaders({
                'apiaccesstoken':
                    'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyZWZlcmVyIjoiaHR0cHM6Ly90ZWFtd29ya3MudGVhbWNvbXB1dGVycy5jb20iLCJpYXQiOjE3NjQwNTE5MjUsImV4cCI6MTc5NDA1MTkyNX0.ZnGB-eASXsIItuRJYCnwHTX-PBfGWqU7ZfVBpsbC6yM'
            });

        return this.http.post<T>(
            this.SiteThrURL + url,
            body,
            {
                headers: headers
            }
        );
    }


    // =========================================================
    // BAM ECR STATUS API
    // DO NOT SEND TEAMWORKS API KEY
    // =========================================================

    postBamECRStatusURL<T = any>(
        body: any
    ): Observable<T> {

        return this.http.post<T>(
            this.SiteBamECRStatusURL,
            body
        );
    }

}